import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';

const LEVEL_COLORS = {
  low:     { bar: 'bg-blue-400',   text: 'text-blue-600',   bg: 'bg-blue-50' },
  medium:  { bar: 'bg-indigo-400', text: 'text-indigo-600', bg: 'bg-indigo-50' },
  high:    { bar: 'bg-amber-400',  text: 'text-amber-700',  bg: 'bg-amber-50' },
  extreme: { bar: 'bg-rose-500',   text: 'text-rose-700',   bg: 'bg-rose-50' },
};

/**
 * Renders a 24-hour bar chart of crowd_score.
 * data: Array<{ hour, label, crowd_score, crowd_level, departures, arrivals }>
 */
export default function HourlyPatternChart({ data = [], title = 'Hour-by-hour crowd pattern', subtitle, dataTestId }) {
  if (!data.length) return null;
  const maxScore = Math.max(...data.map((d) => d.crowd_score), 1);

  return (
    <Card className="border-slate-200 bg-white">
      <CardContent className="p-4 sm:p-6" data-testid={dataTestId}>
        <div className="mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 heading">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-end gap-[3px] sm:gap-1 h-32 sm:h-40">
          {data.map((d, i) => {
            const meta = LEVEL_COLORS[d.crowd_level] || LEVEL_COLORS.medium;
            const heightPct = Math.max(8, (d.crowd_score / maxScore) * 100);
            return (
              <motion.div
                key={d.hour}
                initial={{ height: 0 }}
                animate={{ height: `${heightPct}%` }}
                transition={{ duration: 0.6, delay: i * 0.012, ease: 'easeOut' }}
                className={`flex-1 rounded-t ${meta.bar} hover:opacity-80 cursor-default relative group`}
                title={`${d.label}: ${d.crowd_level} (${d.crowd_score})`}
              >
                <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  {d.label}: {d.crowd_score}
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] sm:text-[11px] text-slate-400 mt-2">
          <span>12 AM</span>
          <span>6 AM</span>
          <span>12 PM</span>
          <span>6 PM</span>
          <span>11 PM</span>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-400"></span>Low</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-indigo-400"></span>Medium</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400"></span>High</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-500"></span>Extreme</span>
        </div>
      </CardContent>
    </Card>
  );
}
